import React from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import { ERP_BRAND, entityId, formatDateTime, formatMoney, projectProductCode, projectProductName } from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { useListScrollRestoration, usePersistentListValue } from "../../../utils/listViewState";

export default function ProjectErpStockPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { access } = useOutletContext();
  const canManageStock = hasProjectPermission(access, "erp.stock.manage");
  const [stockMovements, setStockMovements] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = usePersistentListValue(`erp-stock:${projectId}:page`, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue(`erp-stock:${projectId}:page-size`, 25);
  const [filters, setFilters] = usePersistentListValue(`erp-stock:${projectId}:filters`, { productId: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    productsApi.list(projectId).then((data) => setProducts(data.products || [])).catch(() => setProducts([]));
  }, [projectId]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listStockMovements(projectId, { page: page + 1, pageSize: rowsPerPage, ...filters });
      setStockMovements(data.stockMovements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudo cargar la trazabilidad de inventario");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => { load(); }, [load]);

  useListScrollRestoration(`erp-stock:${projectId}`, !loading);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Stock"
        description="Entradas, salidas y ajustes con el saldo antes y después."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
            {canManageStock ? (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1&tipo=stock_adjustment`)}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Ajustar stock
              </Button>
            ) : null}
          </Stack>
        }
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
        <ContextualHelp
          compact
          title="Cómo leer el stock"
          text="Cada registro conserva el saldo anterior, la variación y el saldo posterior. Las correcciones se hacen como movimientos para no perder trazabilidad."
          bullets={["Los ajustes no borran movimientos anteriores.", "El costo y el usuario quedan disponibles en el detalle de cada registro."]}
        />
      </Box>

      <ErpPanel>
        <Box sx={{ p: { xs: 1.1, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <ResponsiveListControls
            search={
              <Box sx={{ display: "flex", alignItems: "center", minHeight: 40, flex: 1 }}>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, fontWeight: 800 }}>
                  {total} {total === 1 ? "movimiento" : "movimientos"}
                </Typography>
              </Box>
            }
            activeFilterCount={[filters.productId, filters.dateFrom, filters.dateTo].filter(Boolean).length}
            onClear={() => {
              setFilters({ productId: "", dateFrom: "", dateTo: "" });
              setPage(0);
            }}
            drawerTitle="Filtrar inventario"
          >
            <TextField select size="small" label="Producto" value={filters.productId} onChange={(event) => setFilter("productId", event.target.value)} sx={{ minWidth: { sm: 320 } }}>
              <MenuItem value="">Todos los productos</MenuItem>
              {products.map((product) => <MenuItem key={entityId(product)} value={entityId(product)}>{projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </ResponsiveListControls>
        </Box>
        {loading ? <ErpLoading label="Cargando movimientos de inventario..." /> : !stockMovements.length ? <ErpEmpty title="Sin movimientos de inventario" description="Las ventas, compras, entradas, salidas y ajustes confirmados aparecerán aquí." /> : (
          <>
            <TableContainer>
              <ResponsiveOperationalTable mobileLabel="Movimientos de stock" collapseAfter={4}>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Existencia anterior</TableCell>
                    <TableCell align="right">Variación</TableCell>
                    <TableCell align="right">Existencia posterior</TableCell>
                    <TableCell align="right">Costo unitario</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockMovements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>{movement.productNameSnapshot}</TableCell>
                      <TableCell>{movement.kind === "in" ? "Entrada" : movement.kind === "out" ? "Salida" : movement.kind === "adjustment" ? "Ajuste" : "Reversa"}</TableCell>
                      <TableCell align="right">{movement.stockBefore}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950, color: Number(movement.quantityDelta) >= 0 ? "success.main" : "error.main" }}>{Number(movement.quantityDelta) > 0 ? `+${movement.quantityDelta}` : movement.quantityDelta}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{movement.stockAfter}</TableCell>
                      <TableCell align="right">{formatMoney(movement.unitCost)}</TableCell>
                      <TableCell>{movement.createdByName || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>
    </Stack>
  );
}
